import React from 'react';
import { jsx } from '@emotion/core';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import Modal from '../../../components/modal/Modal';
import Input from '../../../components/input/Input';
import { IModal } from '../../../utils/types';
import theme from '../../../theme/theme';
import UpdateHandlers from '../../../utils/handlers';
import {
  useUpdate_Contribution_SourceMutation,
  useAdd_Contribution_SourceMutation,
} from '../../../generated/graphql';
import Flex from '../../../components/layout/Flex';
import ErrorMessage from '../../../components/ErrorMessage';
import { useAuthState } from '../../../context/auth';
/**@jsx jsx*/

interface ValueProps {
  name: string;
}

type ContributionSourceModal = IModal & {
  contributionSource?: { id: number; name: string };
};

const ContributionSourceSchema = Yup.object().shape({
  name: Yup.string().required('required'),
});

const ContributionSourceModal: React.FC<ContributionSourceModal> = ({
  onClose,
  contributionSource,
}) => {
  const { userId } = useAuthState();
  const [initialValues, setInitialValues] = React.useState<ValueProps>({
    name: '',
  });

  const [
    addContributionSource,
    {
      data: addContributionSourceResult,
      loading: addContributionSourceLoading,
      error: addContributionSourceError,
    },
  ] = useAdd_Contribution_SourceMutation();

  const [
    updateContributionSource,
    {
      data: updateContributionSourceResult,
      loading: updateContributionSourceLoading,
      error: updateContributionSourceError,
    },
  ] = useUpdate_Contribution_SourceMutation();

  React.useEffect(() => {
    if (contributionSource) {
      setInitialValues({
        name: contributionSource.name,
      });
    }
  }, [contributionSource]);

  const addNewContributionSource = (values: ValueProps) => {
    addContributionSource({
      variables: {
        changes: [
          {
            name: values.name,
            created_by: userId,
          },
        ],
      },
      update: UpdateHandlers.addContributionSource,
    }).catch(() => {});
  };

  const upateExistingContributionSource = (values: ValueProps) => {
    updateContributionSource({
      variables: {
        id: contributionSource?.id,
        changes: {
          name: values.name,
          modified_by: userId,
        },
      },
    }).catch(() => {});
  };

  const handleSubmit = (
    values: ValueProps,
    { setSubmitting }: FormikHelpers<ValueProps>
  ) => {
    if (contributionSource) {
      upateExistingContributionSource(values);
    } else {
      addNewContributionSource(values);
    }
    setSubmitting(false);
  };

  const loading = !!(
    addContributionSourceLoading || updateContributionSourceLoading
  );
  const error = !!(addContributionSourceError || updateContributionSourceError);
  const success = !!(
    addContributionSourceResult?.insert_contribution_source?.returning
      ?.length ||
    updateContributionSourceResult?.update_contribution_source?.returning
      ?.length
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={ContributionSourceSchema}
      enableReinitialize>
      {(formikProps) => (
        <Form>
          <Modal
            onClose={onClose}
            cancel={{ label: 'Cancel', function: onClose }}
            confirm={{
              type: 'submit',
              label: `${contributionSource ? 'Update' : 'Add'}`,
              loading: formikProps.isSubmitting || loading,
              icon: {
                name: 'plus',
                color: theme.colors.white,
                position: 'after',
              },
            }}
            icon={{ name: 'building-columns' }}
            title={`${
              contributionSource ? 'Update' : 'Add'
            } Contribution Source`}>
            <React.Fragment>
              {error && (
                <Flex
                  jc="center"
                  css={{ color: theme.colors.red[500] }}
                  className="add-failed">
                  Operation failed...please try again
                </Flex>
              )}
              {success && (
                <Flex
                  jc="center"
                  css={{ color: theme.colors.green[500] }}
                  className="add-success">
                  {`Contribution Source ${
                    contributionSource ? 'updated' : 'added'
                  } successfully...`}
                </Flex>
              )}
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Enter name"
                  label="Contribution Source"
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  value={formikProps.values.name}
                />
                <ErrorMessage name="name" />
              </div>
            </React.Fragment>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default ContributionSourceModal;
